import type { ConvertedMetadata, EntitySet, EntityType, ResolutionTarget } from '@sap-ux/vocabularies-types';
import type {
    File,
    ImportProjectParameters,
    GenerateAppSchemaParameters,
    ExtensionLogger,
    ImportAllConfigsParameters,
    Schemas
} from '@sap/ux-specification-types';
import {
    LogSeverity,
    TemplateType,
    v2,
    v4,
    DirName,
    FileName,
    UIVOCABULARY,
    FioriElementsVersion,
    ManifestSection,
    SchemaType,
    Features,
    MANIFESTPATH
} from '@sap/ux-specification-types';
import {
    generateAnalyticalListReportSchemaV2,
    generateListReportSchemaV2,
    generateObjectPageSchemaV2,
    generateOVPInterfaceV2
} from '../v2/generate';
import { getPages as getPagesV2, V2AppProvider } from '../v2/import/app/appProvider';
import { V2OvpProvider } from '../v2/import/app/ovpProvider';
import { generateObjectPageSchemaV4, generateListReportSchemaV4, generateMacrosSchemaDefinition } from '../v4/generate';
import { getPageCustomViewFile } from '../v4/utils/utils';
import { getPages as getPagesV4, V4AppProvider } from '../v4/import/app';
import type { AppProvider } from './appProvider';
import {
    findAlias,
    getSchemaFilePath,
    getTemplateTypeFromManifest,
    getFEVersionFromManifest,
    parseAndMergeAndConvert,
    isFeatureSupported,
    determineV4PageTemplateType,
    getODataVersionFromManifest
} from './utils';
import i18next from 'i18next';
import { log } from '../../extensionLogger';
import { initI18n } from '../../i18n/i18n';
import { getGenericSchema } from '../../specification/schemaAccess';
import { DistTagEvaluator } from './distTagEvaluator';
import { generateAppSchemaV4 } from '../v4/generate/app';

interface UiEntityTypes {
    Facets: string[];
    LineItems: string[];
}

/**
 * Generates an object comprising all generic schemas.
 *
 * @param {TemplateType} templateType - Template type
 * @param {FioriElementsVersion} fioriElementsVersion - Fiori Elements version
 * @returns {object} genericSchemas
 */
async function getGenericSchemas(
    templateType: TemplateType,
    fioriElementsVersion: FioriElementsVersion
): Promise<Schemas> {
    const genericSchemas: Schemas = {};
    let schemaTypes = [];
    schemaTypes = [SchemaType.Application];
    switch (templateType) {
        case TemplateType.ListReportObjectPageV4:
        case TemplateType.AnalyticalListPageV4:
            schemaTypes.push(SchemaType.ListReport, SchemaType.ObjectPage, SchemaType.FPMCustomPage);
            break;
        case TemplateType.FreestylePageV4:
            schemaTypes.push(SchemaType.FreestylePage);
            if (fioriElementsVersion === FioriElementsVersion.v4) {
                schemaTypes.push(SchemaType.ListReport, SchemaType.ObjectPage, SchemaType.FPMCustomPage);
            }
            break;
        case TemplateType.ListReportObjectPageV2:
            schemaTypes.push(SchemaType.ListReport, SchemaType.ObjectPage);
            break;
        case TemplateType.AnalyticalListPageV2:
            schemaTypes.push(SchemaType.AnalyticalListPage, SchemaType.ObjectPage);
            break;
        case TemplateType.OverviewPageV2:
            schemaTypes.push(SchemaType.OverviewPage);
            break;
        default:
            break;
    }
    for (const schemaType of schemaTypes) {
        const schemaFile = getSchemaFilePath(schemaType);
        genericSchemas[schemaFile.filename] = await getGenericSchema(fioriElementsVersion, schemaType);
    }
    return genericSchemas;
}

/**
 * Get the relevant UI.ListItem and UI.Facet entity sets.
 *
 * @param {ConvertedMetadata} oDataServiceAVT - Annotations as converted by AVT
 * @returns a filtered list of facet and line item annotations
 */
function getAnnotationsForUi(oDataServiceAVT: ConvertedMetadata): UiEntityTypes {
    const filteredEntityTypes: UiEntityTypes = {
        Facets: [],
        LineItems: []
    };
    const allEntityTypes = oDataServiceAVT?.entityTypes;
    if (!allEntityTypes) {
        return filteredEntityTypes;
    }
    const alias = findAlias(UIVOCABULARY, oDataServiceAVT);
    for (const entity of allEntityTypes) {
        if (!entity.annotations || !entity.annotations[alias]) {
            continue;
        }
        const { LineItem: lineItem, Facets: facets } = entity.annotations[alias];
        if (lineItem && !lineItem.qualifier) {
            filteredEntityTypes.LineItems.push(entity.name);
        }
        if (facets && !facets.qualifier) {
            filteredEntityTypes.Facets.push(entity.name);
        }
    }
    return filteredEntityTypes;
}

/**
 * Retrieves the entity set by its name from the provided OData service metadata.
 *
 * @param entitySetName - The name of the entity set to retrieve.
 * @param oDataServiceAVT - The OData service metadata containing entity sets.
 * @returns The entity set matching the provided name, or undefined if not found.
 */
export function getEntitySetByEntitySetName(entitySetName: string, oDataServiceAVT: ConvertedMetadata): EntitySet {
    return oDataServiceAVT?.entitySets && oDataServiceAVT.entitySets.find((es) => es.name === entitySetName);
}

/**
 * Determines the target entitySet and entityType of a contextPath that uses at least one NavigationProperty.
 *
 * @param {ResolutionTarget<any>} resolvedContextPath - contextPath, as resolved by AVT
 * @param {ConvertedMetadata} oDataServiceAVT - service AVT
 * @returns {{ entityType: EntityType; entitySet: EntitySet }} - target entityType and entitySet
 */
function resolveNavigationProperty(
    resolvedContextPath: ResolutionTarget<any>,
    oDataServiceAVT: ConvertedMetadata
): { entityType: EntityType; entitySet: EntitySet } {
    const entityType = resolvedContextPath.target.targetType;
    let entitySet = resolvedContextPath.objectPath[1] as EntitySet;
    for (let index = 2; index < resolvedContextPath.objectPath.length; index++) {
        const element = resolvedContextPath.objectPath[index];
        if (entitySet && element['_type'] === 'NavigationProperty') {
            //Consider containment relations
            const bindingName = element['containsTarget']
                ? `${element['name']}/${element['partner']}`
                : element['name'];

            const navPropertyBinding = entitySet.navigationPropertyBinding?.[bindingName];
            entitySet = oDataServiceAVT.entitySets.find((set) => set.name === navPropertyBinding?.name);
        }
    }
    return { entityType, entitySet };
}

/**
 * The function returns an EntityType, EntitySet based on the given contextPath or entitySet.
 *
 * @param {ConvertedMetadata} oDataServiceAVT service AVT
 * @param {string} contextPath contextPath of a page
 * @param {string} entitySetName entitySet of a page
 * @param {ExtensionLogger} [logger] - Logger class for logging messages
 * @returns {EntityType} entityType of a page
 */
export function getEntityTypeEntitySetFromContextPath(
    oDataServiceAVT: ConvertedMetadata,
    contextPath?: string,
    entitySetName?: string,
    logger?: ExtensionLogger
): { entityType: EntityType; entitySet: EntitySet } {
    const resolvedContextPath: ResolutionTarget<any> = oDataServiceAVT?.resolvePath(contextPath || `/${entitySetName}`);
    let entityType: EntityType, entitySet: EntitySet;

    if (resolvedContextPath?.target?._type === 'EntitySet') {
        entityType = (resolvedContextPath.target as EntitySet)?.entityType;
        entitySet = resolvedContextPath?.target as EntitySet;
    } else if (resolvedContextPath?.target?._type === 'NavigationProperty') {
        ({ entityType, entitySet } = resolveNavigationProperty(resolvedContextPath, oDataServiceAVT));
    } else {
        log(logger, {
            severity: LogSeverity.Error,
            message: i18next.t('CONTEXTPATHCOULDNOTBERESOLVED', { entitySet: entitySetName, contextPath }),
            location: {
                path: MANIFESTPATH
            }
        });
    }
    if (entitySetName && !entityType) {
        log(logger, {
            severity: LogSeverity.Error,
            message: i18next.t('NOENTITYTYPE4SET', { entitySetName: entitySetName })
        });
    }
    return { entityType, entitySet };
}

/**
 * The function generates one app schema per entitySet that is linked with an object page in V2.
 *
 * @param generateParameters - list of API input parameters
 * @param genericSchemas - The generic schemas
 * @param appSchemas - the list of app schemas that shall get extended
 */
function generateObjectPageSchemasV2(
    generateParameters: GenerateAppSchemaParameters,
    genericSchemas: Schemas,
    appSchemas: Schemas
): void {
    const allPages = getPagesV2(
        generateParameters.manifest[ManifestSection.generic].pages as v2.SapUiAppPagesV2,
        v2.FE_TEMPLATE_V2_OBJECT_PAGE,
        generateParameters.logger
    );
    for (const page in allPages) {
        const entitySetName = allPages[page].entitySet;
        const entitySet = getEntitySetByEntitySetName(entitySetName, generateParameters.serviceAVT);
        if (!entitySet) {
            log(generateParameters.logger, {
                severity: LogSeverity.Error,
                message: i18next.t('ENTITYSETNOTFOUND', { entitySetName: entitySetName })
            });
        }
        generateParameters.entitySet = entitySet;
        const schemaFile = getSchemaFilePath(SchemaType.ObjectPage, entitySet?.name || entitySetName);
        const genericSchemaFile = getSchemaFilePath(SchemaType.ObjectPage);
        appSchemas[schemaFile.filename] = generateObjectPageSchemaV2(
            generateParameters,
            genericSchemas[genericSchemaFile.filename]
        );
    }
}

/**
 * The function generates one app schema per entitySet that is linked with a list report/alp page in V4.
 *
 * @param {GenerateAppSchemaParameters} generateParameters - list of API input parameters
 * @param {Schemas} genericSchemas - The generic schemas
 * @param {Schemas} appSchemas - the list of app schemas that shall get extended
 */
function generateListReportSchemasV4(
    generateParameters: GenerateAppSchemaParameters,
    genericSchemas: Schemas,
    appSchemas: Schemas
) {
    const lrPages = getPagesV4(generateParameters.manifest, v4.FE_TEMPLATE_V4_LIST_REPORT);
    const alpPages = getPagesV4(generateParameters.manifest, v4.FE_TEMPLATE_V4_ALP);
    const pages = { ...lrPages, ...alpPages };
    const serviceAVT = generateParameters?.serviceAVT;
    for (const pageId in pages) {
        const entitySetName = pages[pageId].options.settings?.entitySet;
        const contextPath = pages[pageId].options.settings?.contextPath as string;
        const { entitySet, entityType } = getEntityTypeEntitySetFromContextPath(
            serviceAVT,
            contextPath,
            entitySetName,
            generateParameters.logger
        );
        generateParameters.entityType = entityType;
        generateParameters.entitySet = entitySet;
        const schemaFile = getSchemaFilePath(SchemaType.ListReport, pageId);
        const genericSchemaFile = getSchemaFilePath(SchemaType.ListReport);
        // Get ALP/LR template type for existing LR page in freestyle app
        if (generateParameters.templateType === TemplateType.FreestylePageV4) {
            generateParameters.templateType = determineV4PageTemplateType(pages[pageId]);
        }
        appSchemas[schemaFile.filename] = generateListReportSchemaV4(
            generateParameters,
            genericSchemas[genericSchemaFile.filename],
            pages[pageId].name,
            contextPath
        );
    }
}
/**
 * Looks for List Report(s) in a V2 manifest and triggers the schema generation.
 *
 * @param {GenerateAppSchemaParameters} generateParameters - list of API input parameters
 * @param {Schemas} appSchemas - the list of app schemas that shall get extended
 * @param {Schemas} genericSchemas - The generic schemas
 */
function generateAppSchemaV2LR(
    generateParameters: GenerateAppSchemaParameters,
    appSchemas: Schemas,
    genericSchemas: Schemas
) {
    const allPages = getPagesV2(
        generateParameters.manifest[ManifestSection.generic].pages as v2.SapUiAppPagesV2,
        v2.FE_TEMPLATE_V2_LIST_REPORT,
        generateParameters.logger
    );

    for (const page in allPages) {
        //Should only be one entry
        const entitySetName = allPages[page].entitySet;
        generateParameters.entitySet = getEntitySetByEntitySetName(entitySetName, generateParameters.serviceAVT);
        const schemaFile = getSchemaFilePath(
            SchemaType.ListReport,
            generateParameters.entitySet?.name || entitySetName
        );
        const genericSchemaFile = getSchemaFilePath(SchemaType.ListReport);
        appSchemas[schemaFile.filename] = generateListReportSchemaV2(
            generateParameters,
            genericSchemas[genericSchemaFile.filename]
        );
    }
}

/**
 * The function generates one app schema per entitySet that is linked with an object page in V4.
 *
 * @param {GenerateAppSchemaParameters} generateParameters - list of API input parameters
 * @param {Schemas} genericSchemas - The generic schemas
 * @param {Schemas} appSchemas - the list of app schemas that shall get extended
 */
function generateObjectPageSchemasV4(
    generateParameters: GenerateAppSchemaParameters,
    genericSchemas: Schemas,
    appSchemas: Schemas
): void {
    const pages = getPagesV4(generateParameters.manifest, v4.FE_TEMPLATE_V4_OBJECT_PAGE);
    const serviceAVT = generateParameters.serviceAVT;
    for (const page in pages) {
        const entitySetName = pages[page].options.settings?.entitySet;
        const contextPath = pages[page].options.settings?.contextPath as string;

        const { entityType, entitySet } = getEntityTypeEntitySetFromContextPath(serviceAVT, contextPath, entitySetName);
        generateParameters.entitySet = entitySet;
        generateParameters.entityType = entityType;

        const schemaFile = getSchemaFilePath(SchemaType.ObjectPage, page);
        const genericSchemaFile = getSchemaFilePath(SchemaType.ObjectPage);
        const genericMacroSchemaFile = getSchemaFilePath(SchemaType.FPMCustomPage);

        appSchemas[schemaFile.filename] = generateObjectPageSchemaV4(
            generateParameters,
            genericSchemas[genericSchemaFile.filename],
            contextPath,
            genericSchemas[genericMacroSchemaFile.filename]
        );
    }
}

/**
 * Generates all app specific schemas for a Fiori Elements V2 application.
 *
 * @param generateParameters - list of API input parameters
 * @param genericSchemas - The generic schemas
 * @param uiEntityTypes - list of all entity types, as comprised in oDataServiceAVT
 * @param templateType - the given Fiori Elements' template type
 * @returns The generated app schemas including generic schemas.
 */
function generateAppSchemasV2(
    generateParameters: GenerateAppSchemaParameters,
    genericSchemas: Schemas,
    uiEntityTypes: UiEntityTypes,
    templateType: TemplateType
): Schemas {
    const appSchemas: Schemas = {};
    if (templateType === TemplateType.ListReportObjectPageV2 && uiEntityTypes) {
        /*--- List Report ----*/
        generateAppSchemaV2LR(generateParameters, appSchemas, genericSchemas);
        /*--- Object Pages ----*/
        generateObjectPageSchemasV2(generateParameters, genericSchemas, appSchemas);
    } else if (templateType === TemplateType.AnalyticalListPageV2 && uiEntityTypes) {
        /*--- Analytical List Page ----*/
        const allPages = getPagesV2(
            generateParameters.manifest[ManifestSection.generic].pages as v2.SapUiAppPagesV2,
            v2.FE_TEMPLATE_V2_ALP,
            generateParameters.logger
        );
        for (const page in allPages) {
            const entitySetName = allPages[page].entitySet;
            generateParameters.entitySet = getEntitySetByEntitySetName(entitySetName, generateParameters.serviceAVT);
            const schemaFile = getSchemaFilePath(
                SchemaType.AnalyticalListPage,
                generateParameters.entitySet?.name || entitySetName
            );
            const genericSchemaFile = getSchemaFilePath(SchemaType.AnalyticalListPage);
            appSchemas[schemaFile.filename] = generateAnalyticalListReportSchemaV2(
                generateParameters,
                genericSchemas[genericSchemaFile.filename]
            );
        }
        /*--- Object Pages ----*/
        generateObjectPageSchemasV2(generateParameters, genericSchemas, appSchemas);
    } else if (templateType === TemplateType.OverviewPageV2) {
        /*--- Overview Page ----*/
        const schemaFile = getSchemaFilePath(SchemaType.OverviewPage);
        const genericSchemaFile = getSchemaFilePath(SchemaType.OverviewPage);
        appSchemas[schemaFile.filename] = generateOVPInterfaceV2(
            genericSchemas[genericSchemaFile.filename],
            generateParameters.manifest
        );
    }
    return { ...genericSchemas, ...appSchemas };
}

/**
 * The function generates one app schema per custom page in V4.
 *
 * @param {GenerateAppSchemaParameters} generateParameters List of API input parameters.
 * @param {Schemas} genericSchemas The generic schemas.
 * @param {Schemas} appSchemas The list of app schemas that shall get extended.
 */
function generateCustomPageSchemasV4(
    generateParameters: GenerateAppSchemaParameters,
    genericSchemas: Schemas,
    appSchemas: Schemas
): void {
    if (!isFeatureSupported(generateParameters.featureToggles, Features.BuildingBlocks)) {
        return;
    }
    const pages = getPagesV4(generateParameters.manifest, v4.FE_TEMPLATE_V4_CUSTOM_PAGE);
    for (const pageId in pages) {
        const page = pages[pageId];
        // Resolve view name and app id
        const viewData = getPageCustomViewFile(
            generateParameters.views || [],
            pageId,
            generateParameters.manifest,
            generateParameters.logger
        );
        const viewId = page['viewId'] || page['id'] || pageId;
        if (viewData) {
            const schemaFile = getSchemaFilePath(SchemaType.FPMCustomPage, undefined, viewId);
            const genericSchemaFile = getSchemaFilePath(SchemaType.FPMCustomPage);
            appSchemas[schemaFile.filename] = generateMacrosSchemaDefinition(
                genericSchemas[genericSchemaFile.filename],
                viewData,
                generateParameters.serviceAVT,
                generateParameters.logger
            );
        }
    }
}

/**
 * Generates all app specific schemas for a Fiori Elements V4 application.
 *
 * @param {GenerateAppSchemaParameters} generateParameters - list of API input parameters
 * @param {Schemas} genericSchemas - The generic schemas
 * @param {UiEntityTypes} uiEntityTypes - list of all entity types, as comprised in oDataServiceAVT
 * @returns The generated app schemas including generic schemas.
 */
function generateAppSchemasV4(
    generateParameters: GenerateAppSchemaParameters,
    genericSchemas: Schemas,
    uiEntityTypes: UiEntityTypes
): Schemas {
    const appSchemas: Schemas = {};

    if (uiEntityTypes) {
        generateAppSchemaV4(generateParameters, genericSchemas.App);
        generateListReportSchemasV4(generateParameters, genericSchemas, appSchemas);
        generateObjectPageSchemasV4(generateParameters, genericSchemas, appSchemas);
        generateCustomPageSchemasV4(generateParameters, genericSchemas, appSchemas);
    }
    return { ...genericSchemas, ...appSchemas };
}

/**
 * Generates app specific schemas by adding annotation information to copies of the generic schemas, thus replacing all generic definitions.
 *
 * @param {GenerateAppSchemaParameters} generateParameters - list of API input parameters
 * @param {UiEntityTypes} uiEntityTypes - list of all entity types from the service metadata
 * @param templateType - type of the Fiori Elements' template
 * @returns {Schemas} - the generated app schemas
 */
async function generateAppSchemas(
    generateParameters: GenerateAppSchemaParameters,
    uiEntityTypes: UiEntityTypes,
    templateType: TemplateType
): Promise<Schemas> {
    const genericSchemas = await getGenericSchemas(templateType, generateParameters.fioriElementsVersion);

    if (
        !generateParameters.fioriElementsVersion ||
        generateParameters.fioriElementsVersion === FioriElementsVersion.v4
    ) {
        return generateAppSchemasV4(generateParameters, genericSchemas, uiEntityTypes);
    } else {
        return generateAppSchemasV2(generateParameters, genericSchemas, uiEntityTypes, templateType);
    }
}

/**
 * Imports manifest and flex changes into configuration files with aid of the relevant app provider.
 *
 * @param {ImportAllConfigsParameters} importParameters - structure comprising all input parameters
 * @returns {File[]} a list of all configuration files as generated by the function
 */
function importConfigs(importParameters: ImportAllConfigsParameters): File[] {
    let provider: AppProvider;
    const files: File[] = [];

    switch (importParameters.templateType) {
        case TemplateType.ListReportObjectPageV2:
        case TemplateType.AnalyticalListPageV2:
            provider = new V2AppProvider(importParameters);
            break;
        case TemplateType.AnalyticalListPageV4:
        case TemplateType.ListReportObjectPageV4:
        case TemplateType.FreestylePageV4:
            provider = new V4AppProvider(importParameters);
            break;
        case TemplateType.OverviewPageV2:
            provider = new V2OvpProvider(importParameters);
            break;
        default:
            provider = new V4AppProvider(importParameters);
            break;
    }
    if (provider) {
        const { appConfig, pageConfigs } = provider.createConfigFiles();
        for (const app in appConfig) {
            files.push({ dataSourceUri: app, fileContent: JSON.stringify(appConfig[app], null, 4) });
        }
        for (const config in pageConfigs) {
            files.push({ dataSourceUri: config, fileContent: JSON.stringify(pageConfigs[config], null, 4) });
        }
    }
    return files;
}

/**
 * Generates a list of files based on the provided schemas and optional configuration files.
 *
 * @param appSchemas - The schemas to be converted into files.
 * @param configs - Optional configuration files to be included in the output.
 * @returns A list of files containing schema and configuration data.
 */
function getFileList(appSchemas: Schemas, configs?: File[]): File[] {
    let files: File[] = [];
    for (const schemaName of Object.keys(appSchemas).sort((a, b) => a.localeCompare(b))) {
        let dataSourceUri;
        if (schemaName === SchemaType.Application) {
            dataSourceUri = `${DirName.Schemas}/${FileName.App[0].toUpperCase()}${FileName.App.slice(1)}`;
        } else {
            dataSourceUri = `${DirName.Schemas}/${schemaName}.json`;
        }
        files.push({ dataSourceUri, fileContent: JSON.stringify(appSchemas[schemaName], null, 4) });
    }
    if (configs) {
        files = files.concat(configs);
    }
    return files;
}

/**
 * Import the schema and config files for a given project.
 *
 * @param importProjectParameters - files of the project: manifest, flex changes, odata files
 * @returns {Promise<File[]>} - list of files containing schema and configuration data
 */
export async function importProjectSchema(importProjectParameters: ImportProjectParameters): Promise<File[]> {
    // Initialize i18next
    initI18n();
    const { manifest, annotations, flex, logger, fragments, views, featureToggles } = importProjectParameters;
    const versionChecker = new DistTagEvaluator();
    versionChecker.doesUi5VersionFitDistTags(manifest['sap.ui5']?.dependencies?.minUI5Version, logger);
    const fioriElementsVersion = getFEVersionFromManifest(manifest);
    const oDataServiceAVT = parseAndMergeAndConvert(annotations, logger);
    const uiEntityTypes = getAnnotationsForUi(oDataServiceAVT);
    const templateType = getTemplateTypeFromManifest(manifest, fioriElementsVersion, logger);
    const generateParameters: GenerateAppSchemaParameters = {
        templateType,
        manifest,
        flex,
        serviceAVT: oDataServiceAVT,
        fragments,
        views,
        fioriElementsVersion,
        logger,
        featureToggles
    };

    const appSchemas = await generateAppSchemas(generateParameters, uiEntityTypes, templateType);
    return getFileList(appSchemas);
}

/**
 * Import the schema and config files for a given project.
 *
 * @param {ImportProjectParameters} importProjectParameters - files of the project: manifest, flex changes, odata files
 * @returns {Promise<File[]>} - list of files containing schema and configuration data
 */
export async function importProjectSchemaAndConfig(importProjectParameters: ImportProjectParameters): Promise<File[]> {
    // Initialize i18next
    initI18n();
    const { manifest, annotations, logger, fragments, views, flex, featureToggles } = importProjectParameters;
    const versionChecker = new DistTagEvaluator();
    versionChecker.doesUi5VersionFitDistTags(manifest['sap.ui5']?.dependencies?.minUI5Version, logger);
    const fioriElementsVersion = getFEVersionFromManifest(manifest);
    const oDataVersion = getODataVersionFromManifest(manifest);
    const oDataServiceAVT = parseAndMergeAndConvert(annotations, logger);
    const templateType = getTemplateTypeFromManifest(manifest, fioriElementsVersion, logger);

    //Generate Schemas
    const generateParameters: GenerateAppSchemaParameters = {
        templateType,
        manifest,
        flex,
        serviceAVT: oDataServiceAVT,
        fragments,
        views,
        fioriElementsVersion,
        logger,
        featureToggles
    };
    const uiEntityTypes = getAnnotationsForUi(oDataServiceAVT);
    const appSchemas = await generateAppSchemas(generateParameters, uiEntityTypes, templateType);

    //Import to configs
    const importConfigsParameters: ImportAllConfigsParameters = {
        manifest,
        flexChanges: flex,
        appSchemas,
        templateType,
        logger,
        serviceAVT: oDataServiceAVT,
        fragments,
        views,
        fioriElementsVersion,
        oDataVersion
    };

    const configFiles = importConfigs(importConfigsParameters);
    return getFileList(appSchemas, configFiles);
}
